import { Component } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';
import { JobDetailsV2Fragment } from 'graphql.generated';
import { partition } from 'lodash';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { withLatestFrom, map } from 'rxjs';
import { HistoryService } from 'src/app/history/history.service';
import { OnlineStatusService } from 'src/app/online-status.service';
import { DocumentHelperService } from 'src/app/services/document-helper.service';
import { FreyaHelperService } from 'src/app/services/freya-helper.service';


import { isInvoiceArtifact } from '../../artifacts/artifact.util';
import { PlusAuthenticationService } from '../../core/public-api';
import { FreyaCommonModule } from '../../freya-common/freya-common.module';
import { DetailsHelperService } from '../../services/details-helper.service';
import { JobHeaderDetailsComponent } from '../../shared/job-header-details/job-header-details.component';
import { selectJobPageHasSaveableChanges, selectSaveableJobPageChanges } from '../../shared/unsaved-changes-banner/unsaved-changes-banner.selectors';
import { selectCanChangeArea } from '../job-edit/job-edit-state/job-edit.selectors';
import { JobToolActions } from '../job-tool.actions';
import {
  selectJob,
  selectJobInCurrentZone,
  selectCustomerName,
  selectCustomer,
  selectCompanyName,
  selectJobLoaded,
  selectJobLoading,
  selectJobUpdating,
  selectJobId,
} from '../job-tool.reducer';

import { JobHeaderActions } from './job-header.actions';
import { JobTagsComponent } from './job-tags/job-tags.component';

@Component({
  selector: 'app-job-header',
  standalone: true,
  imports: [
    FreyaCommonModule,
    JobHeaderDetailsComponent,
    JobTagsComponent
  ],
  templateUrl: './job-header.component.html',
  styleUrl: './job-header.component.scss'
})
export class JobHeaderComponent {

  public viewModel$ = this.store.select(createSelector({
    loaded: selectJobLoaded,
    jobLoading: selectJobLoading,
    jobBeingUpdated: selectJobUpdating,
    job: selectJob,
    canChangeArea: selectCanChangeArea,
    customerName: selectCustomerName,
    customer: selectCustomer,
    company: selectCompanyName,
    unsavedChanges: selectJobPageHasSaveableChanges,
    jobNotInZone: createSelector(selectJobInCurrentZone, (inZone) => !inZone),
    saveChangesButtonDisabled: createSelector(
      selectSaveableJobPageChanges,
      (changes) => {

        const [ pendingChanges, newChanges ] = partition(changes, (c) => c.saving);

        return newChanges.length === 0 && pendingChanges.length > 0;
      }
    ),
    saveChangesButtonIcon: createSelector(
      selectSaveableJobPageChanges,
      (changes) => {

        const [ pendingChanges, newChanges ] = partition(changes, (c) => c.saving);

        return newChanges.length === 0 && pendingChanges.length > 0 ? 'pi pi-spin pi-spinner' : 'pi pi-save';
      }
    ),
    actionMenuItems: createSelector(
      selectJob,
      selectCanChangeArea,
      selectJobInCurrentZone,
      (job, canChangeArea, jobInCurrentZone) => this._getActionsMenuItems(job, canChangeArea, jobInCurrentZone),
    ),
    editJobRouterLink: createSelector(
      selectJobId,
      (jobId) => ['/jobs', jobId, 'edit'],
    ),
    editJobLabel: createSelector(
      selectJob,
      (job) => ['lead', 'estimate'].includes(job?.stage) ? 'Open in New Opportunity' : 'Edit Job',
    ),
  })).pipe(
    withLatestFrom(this.onlineStatusService.isOnline$),
    map(([ { unsavedChanges, saveChangesButtonDisabled, ...otherStoreValues }, isOnline ]) => {

      let saveTooltipText: string;

      if (!isOnline) {
        saveTooltipText = 'You are offline. All changes made will be saved locally';
      } else if (unsavedChanges) {
        saveTooltipText = 'Save unsaved changes';
      }

      return {
      ...otherStoreValues,
      saveChangesButtonDisabled: saveChangesButtonDisabled || !isOnline,
      unsavedChanges,
      isOnline,
      saveTooltipText,
      }
    }),
  );

  public actions = JobHeaderActions;

  constructor(
    public store: Store,
    public detailsHelper: DetailsHelperService,
    public authSvc: PlusAuthenticationService,
    public documentHelper: DocumentHelperService,
    public historyService: HistoryService,
    public dialogService: DialogService,
    private freyaHelper: FreyaHelperService,
    private onlineStatusService: OnlineStatusService,
  ) {}

  private _getActionsMenuItems(job: JobDetailsV2Fragment, canChangeArea: boolean, jobInCurrentZone: boolean): MenuItem[] {

    if (!job) { return []; }

    const isClosed = Boolean(job?.closedAt);

    const { disableClose, reason } = this.freyaHelper.getJobCloseActionState(job, undefined, true);

    const invoiceDocuments = job.artifacts.filter((a) => isInvoiceArtifact(a));

    return [
      {
        id: 'edit',
        label: 'Edit Job',
        icon: 'pi pi-pencil',
        routerLink: ['/jobs', job.id, 'edit'],
        disabled: !jobInCurrentZone,
      },
      {
        id: 'download-invoice',
        label: 'Download latest invoice',
        icon: 'pi pi-file-o',
        command: () => this.store.dispatch(this.actions.downloadLatestInvoiceMenuItemClicked()),
        visible: invoiceDocuments.length > 0,
      },
      {
        id: 'print-bols',
        label: 'Print all BOLs',
        icon: 'pi pi-print',
        command: () => this.store.dispatch(JobToolActions.printAllBolsRequested({ jobId: job.id })),
      },
      {
        id: 'open-job',
        label: 'Open',
        icon: 'pi pi-circle',
        disabled: disableClose,
        tooltip: reason,
        command: () => this.store.dispatch(this.actions.openJobMenuItemClicked()),
        visible: isClosed,
      },
      {
        id: 'close-job',
        label: 'Close',
        icon: 'pi pi-circle-fill',
        disabled: disableClose,
        tooltip: reason,
        command: () => this.store.dispatch(this.actions.closeJobMenuItemClicked()),
        visible: !isClosed,
      },
      {
        id: 'view-history',
        label: 'View History',
        icon: 'pi pi-book',
        command: () => this.store.dispatch(this.actions.viewJobHistoryMenuItemClicked()),
      },
      {
        id: 'send-documents',
        label: 'Send Document',
        icon: 'pi pi-send',
        disabled: false,
        command: () => this.store.dispatch(this.actions.sendDocumentMenuItemClicked()),
      },
      {
        id: 'change-area',
        label: 'Change Area',
        icon: 'pi pi-map',
        disabled: !jobInCurrentZone || !canChangeArea,
          tooltip: !jobInCurrentZone
          ? 'This job can only be edited from its original zone'
          : (!canChangeArea ? 'Remove all transactions to change' : undefined),
        command: () => this.store.dispatch(this.actions.changeAreaMenuItemClicked()),
      },
      {
        id: 'use-calculator-from-zone-settings',
        label: 'Use Calculator from Zone Settings',
        icon: 'pi pi-refresh',
        command: () => this.store.dispatch(this.actions.useCalculatorFromZoneSettingsMenuItemClicked()),
      },
      {
        id: 'delete',
        label: 'Delete Job',
        icon: 'pi pi-trash',
        command: () => this.store.dispatch(this.actions.deleteJobMenuItemClicked()),
        visible: isClosed,
      },
    ];
  }
}
